import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ConditionValue,
  IRulesetSchemaFieldChoice,
  IRulesetSchemaOperator,
} from '../../../types/ruleset-types';

type ValueType = ConditionValue | undefined;

interface ValueInputProps {
  value: ValueType;
  operator: IRulesetSchemaOperator | null;
  choices?: IRulesetSchemaFieldChoice[];
  fieldType?: string;
  onChange: (value: ConditionValue) => void;
  disabled?: boolean;
}

const clampNonNegative = (value: number): number => {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
};

const splitPastedTokens = (text: string, separator: RegExp): string[] => {
  return text
    .split(separator)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
};

interface RegexListInputProps {
  patterns: string[];
  onChange: (patterns: string[]) => void;
  disabled?: boolean;
}

const RegexListInput: React.FC<RegexListInputProps> = ({ patterns, onChange, disabled }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleChipClick = (index: number) => {
    if (disabled) return;
    setEditingIndex(index);
    setEditValue(patterns[index]);
  };

  const handleEditComplete = () => {
    if (editingIndex === null) return;
    const trimmed = editValue.trim();
    if (trimmed) {
      const newPatterns = [...patterns];
      newPatterns[editingIndex] = trimmed;
      onChange(newPatterns);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditComplete();
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleDelete = (index: number) => {
    onChange(patterns.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditValue('');
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={[]}
      value={patterns}
      onChange={(_, newValue) => onChange(newValue as string[])}
      disabled={disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          if (editingIndex === index) {
            return (
              <TextField
                key={`edit-${index}`}
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleEditComplete}
                onKeyDown={handleEditKeyDown}
                autoFocus
                sx={{
                  width: 200,
                  '& input': { fontFamily: 'monospace', fontSize: '12px', py: 0.5 },
                }}
              />
            );
          }
          const tagProps = getTagProps({ index });
          return (
            <Tooltip key={option} title={option} placement="top" enterDelay={300}>
              <Chip
                {...tagProps}
                label={option}
                size="small"
                onClick={() => handleChipClick(index)}
                onDelete={() => handleDelete(index)}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  maxWidth: 180,
                  cursor: disabled ? 'default' : 'pointer',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Tooltip>
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Patterns"
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            const tokens = splitPastedTokens(text, /[\r\n]+/);
            if (tokens.length <= 1) return;
            e.preventDefault();
            const merged = Array.from(new Set([...patterns, ...tokens]));
            onChange(merged);
          }}
          sx={{ '& input': { fontFamily: 'monospace' } }}
        />
      )}
      sx={{ width: '100%', minWidth: 0 }}
      data-cy="autocomplete-condition-regex-list"
    />
  );
};

const ValueInput: React.FC<ValueInputProps> = ({
  value,
  operator,
  choices,
  fieldType,
  onChange,
  disabled = false,
}) => {
  const normalizedFieldType = (fieldType || '').toLowerCase();
  const isNumericField =
    normalizedFieldType === 'integer' ||
    normalizedFieldType === 'number' ||
    normalizedFieldType === 'int' ||
    normalizedFieldType === 'float' ||
    normalizedFieldType === 'decimal';
  const isBooleanField =
    normalizedFieldType === 'boolean' || normalizedFieldType === 'bool';

  const parseScalarValue = (rawValue: string): string | number | boolean | null => {
    if (rawValue === '') {
      return null;
    }

    if (isBooleanField) {
      return rawValue === 'true';
    }

    if (isNumericField) {
      return clampNonNegative(Number(rawValue));
    }

    return rawValue;
  };

  if (!operator) {
    return (
      <FormControl size="small" sx={{ width: '100%', minWidth: 0 }}>
        <Select
          disabled
          displayEmpty
          variant="outlined"
          value=""
          sx={{ width: '100%', fontSize: '13px' }}
        >
          <MenuItem value="" disabled>
            <em>Value</em>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  const { value_type } = operator;

  // No value needed for these operators
  if (value_type === 'none') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', px: 1 }}>
        No value needed
      </Typography>
    );
  }

  // Scalar value with choices - render as searchable autocomplete
  if (value_type === 'scalar' && choices && choices.length > 0) {
    const selectedChoice =
      value === null || value === undefined
        ? null
        : choices.find((item) => String(item.value) === String(value)) ?? null;
    return (
      <Autocomplete
        size="small"
        options={choices}
        value={selectedChoice}
        onChange={(_, newValue) =>
          onChange((newValue?.value as string | number | undefined) ?? null)
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Value"
            data-cy="select-condition-value"
            inputProps={{ ...params.inputProps, style: { fontSize: '13px' } }}
          />
        )}
        sx={{ width: '100%', minWidth: 0 }}
        data-cy="autocomplete-condition-value"
      />
    );
  }

  // Scalar value - render as text/number input
  if (value_type === 'scalar') {
    if (isBooleanField) {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 0 }}>
          <Select
            value={typeof value === 'boolean' ? String(value) : ''}
            onChange={(e: SelectChangeEvent<string>) => onChange(parseScalarValue(e.target.value))}
            disabled={disabled}
            displayEmpty
            variant="outlined"
            data-cy="select-condition-value-boolean"
            sx={{ width: '100%', fontSize: '13px' }}
          >
            <MenuItem value="" disabled>
              <em>Value</em>
            </MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        size="small"
        variant="outlined"
        placeholder="Value"
        type={isNumericField ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => {
          onChange(parseScalarValue(e.target.value));
        }}
        disabled={disabled}
        sx={{ width: '100%', minWidth: 0 }}
        inputProps={{
          min: 0,
          style: { fontSize: '13px' },
        }}
        data-cy="input-condition-value"
      />
    );
  }

  // List value with choices - render as multi-select autocomplete
  if (value_type === 'list' && choices && choices.length > 0) {
    const selectedValues = Array.isArray(value)
      ? choices.filter((choice) => (value as Array<string | number>).includes(choice.value))
      : [];
    return (
      <Autocomplete
        multiple
        size="small"
        options={choices}
        getOptionLabel={(option) => option.label}
        value={selectedValues}
        onChange={(_, newValue) => onChange(newValue.map((choice) => choice.value))}
        disabled={disabled}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            return (
              <Chip
                {...getTagProps({ index })}
                key={String(option.value)}
                label={option.label}
                size="small"
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="Values" />
        )}
        sx={{ width: '100%', minWidth: 0 }}
        data-cy="autocomplete-condition-values"
      />
    );
  }

  // List value without choices - render as free-form tag input
  if (value_type === 'list') {
    const selectedValues = Array.isArray(value)
      ? (value as Array<string | number>).map((item) => String(item))
      : [];
    return (
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={[]}
        value={selectedValues}
        onChange={(_, newValue) =>
          onChange(
            newValue.map((item) => {
              const rawValue = String(item).trim();
              return isNumericField && rawValue !== ''
                ? clampNonNegative(Number(rawValue))
                : rawValue;
            })
          )
        }
        disabled={disabled}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Type + Enter, or paste a list"
            onPaste={(e) => {
              const text = e.clipboardData.getData('text');
              const tokens = splitPastedTokens(text, /[\r\n\t]+/);
              if (tokens.length <= 1) return;
              e.preventDefault();
              const normalized: Array<string | number> = tokens.map((token) =>
                isNumericField ? clampNonNegative(Number(token)) : token
              );
              const existing: Array<string | number> = Array.isArray(value)
                ? (value as Array<string | number>)
                : [];
              const existingKeys = new Set(existing.map((v) => String(v)));
              const additions = normalized.filter((v) => !existingKeys.has(String(v)));
              onChange([...existing, ...additions]);
            }}
          />
        )}
        sx={{ width: '100%', minWidth: 0 }}
        data-cy="autocomplete-condition-values-free"
      />
    );
  }

  // Range value - render two number inputs
  if (value_type === 'range') {
    const rangeValue =
      Array.isArray(value) && value.length === 2
        ? (value as [number | null, number | null])
        : [null, null];
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%', minWidth: 0 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Min"
          type="number"
          value={rangeValue[0] ?? ''}
          onChange={(e) => {
            const newMin =
              e.target.value === '' ? null : clampNonNegative(Number(e.target.value));
            onChange([newMin, rangeValue[1]]);
          }}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
          inputProps={{
            min: 0,
            style: { fontSize: '13px' },
          }}
          data-cy="input-condition-range-min"
        />
        <Typography variant="caption" color="text.secondary">to</Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Max"
          type="number"
          value={rangeValue[1] ?? ''}
          onChange={(e) => {
            const newMax =
              e.target.value === '' ? null : clampNonNegative(Number(e.target.value));
            onChange([rangeValue[0], newMax]);
          }}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
          inputProps={{
            min: 0,
            style: { fontSize: '13px' },
          }}
          data-cy="input-condition-range-max"
        />
      </Box>
    );
  }

  // Regex value - render as text input
  if (value_type === 'regex') {
    return (
      <TextField
        size="small"
        variant="outlined"
        placeholder="Regex pattern"
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        sx={{
          width: '100%',
          minWidth: 0,
          '& input': { fontFamily: 'monospace', fontSize: '12px' },
        }}
        data-cy="input-condition-regex"
      />
    );
  }

  // Regex list value
  if (value_type === 'regex_list') {
    const patterns = Array.isArray(value) ? (value as string[]) : [];
    return (
      <RegexListInput
        patterns={patterns}
        onChange={(newPatterns) => onChange(newPatterns)}
        disabled={disabled}
      />
    );
  }

  // Fallback
  return (
    <TextField
      size="small"
      variant="outlined"
      placeholder="Value"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      sx={{ width: '100%', minWidth: 0 }}
      inputProps={{ style: { fontSize: '13px' } }}
    />
  );
};

export default ValueInput;
